/**
 * Everybody waiting on the line for a car.
 *
 * Beside the missed-call queue and shaped like it, because it answers the same kind of
 * question: who is the office currently keeping waiting. The difference is that these people
 * are still on the telephone, so the header carries the count against the firm's line limit
 * — a dispatcher about to park the next caller needs to know whether there is room before
 * they promise anything.
 *
 * Oldest first, from the server. The list is refetched rather than patched from the live
 * message, for the same reason the missed queue is: a client keeping its own copy of a queue
 * is a client that eventually disagrees with the one the server enforces.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { callsApi, type CallSummary } from '../calls/api';
import { useDispatcherHub } from '../realtime/useDispatcherHub';
import { useLineLabel } from './lines';
import { Card, CardHeader, Sub } from '../ui';

export function HeldCalls({ className }: { className?: string }) {
  const { t } = useTranslation();
  const labelFor = useLineLabel();

  const [calls, setCalls] = useState<CallSummary[]>([]);
  const [held, setHeld] = useState(0);
  const [max, setMax] = useState(0);

  // Ticks the "waiting for" column without re-fetching anything. Somebody who has been
  // holding for two minutes is the most important row on this card, and a duration that only
  // moves when a message arrives would sit still for the whole wait.
  const [, tick] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const waiting = await callsApi.held();

      setCalls(waiting.calls);
      setHeld(waiting.held);
      setMax(waiting.max);
    } catch {
      // A card that will not load shows nothing. The console still takes orders, which is
      // what matters.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const waiting = await callsApi.held();

        if (!cancelled) {
          setCalls(waiting.calls);
          setHeld(waiting.held);
          setMax(waiting.max);
        }
      } catch {
        // As above.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (calls.length === 0) return;

    const timer = globalThis.setInterval(() => tick((n) => n + 1), 1000);

    return () => globalThis.clearInterval(timer);
  }, [calls.length]);

  useDispatcherHub({
    onCallHeldChanged: () => void refresh(),
    onReconnected: () => void refresh(),
  });

  // Nothing waiting and nothing to say — which is every console at a firm on the classic
  // flow, always. A card that is there and usually empty is a card dispatchers stop seeing.
  if (calls.length === 0) return null;

  return (
    <Card className={className}>
      <CardHeader
        title={t('held.title')}
        actions={<span className="text-secondary text-ink-3">{t('held.ofLines', { held, max })}</span>}
      />

      <ul className="m-0 p-0 list-none max-h-[320px] overflow-y-auto">
        {calls.map((call) => (
          <li
            key={call.id}
            className="flex items-center justify-between gap-2 px-3.5 py-2 border-b border-rule last:border-b-0"
          >
            <div className="min-w-0">
              <strong className="font-semibold tabular-nums">
                {call.from ?? t('calls.withheld')}
              </strong>
              {call.line !== null && <Sub block>{labelFor(call.line)}</Sub>}
            </div>

            <span className="text-warn tabular-nums shrink-0">
              {t('held.waitingFor', { time: waited(call.heldAt) })}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** How long somebody has been waiting, as minutes and seconds. */
function waited(heldAt: string | null): string {
  if (heldAt === null) return '—';

  const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(heldAt)) / 1000));

  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
